import axios from 'axios'
import {normalizeExtensionRequestsList} from '../extensionRequestUtils'
import type {BuildDigitalVosFormInput} from './digitalVosFormTypes'
import {
  fetchAppointmentCheckInAudit,
  hasCheckInStaffAudit,
  mergeCheckInAuditFromAppointment,
} from './fetchAppointmentCheckInAudit'
import {isDigitalVosApproved} from './buildDigitalVosFormData'

const API_URL = process.env.REACT_APP_API_URL

export type DigitalVosFormPayloadResult =
  | {ok: true; input: BuildDigitalVosFormInput; appointmentCode: string}
  | {ok: false; status: number; message: string}

export async function fetchDigitalVosFormPayload(
  appointmentId: string | number,
  options?: {requireApproved?: boolean},
): Promise<DigitalVosFormPayloadResult> {
  const requireApproved = options?.requireApproved !== false
  try {
    const [appointmentRes, osiRes] = await Promise.all([
      axios.get(`${API_URL}/Appoinment/${appointmentId}`),
      axios.get(`${API_URL}/Appoinment/osi-submission-appointment/${appointmentId}`),
    ])

    let appointment = (appointmentRes.data ?? {}) as Record<string, unknown>
    const meetingSummary = (osiRes.data ?? {}) as Record<string, unknown>

    if (!meetingSummary?.code && !meetingSummary?.Code) {
      return {ok: false, status: 404, message: 'No VOS submission found for this appointment.'}
    }

    const vosMode = Number(meetingSummary.vosSubmissionMode ?? meetingSummary.VosSubmissionMode)
    if (vosMode !== 1) {
      return {
        ok: false,
        status: 403,
        message: 'This appointment does not use digital VOS submission.',
      }
    }

    if (requireApproved && !isDigitalVosApproved(meetingSummary)) {
      return {
        ok: false,
        status: 403,
        message: 'Digital VOS form is only available after admin approval.',
      }
    }

    if (!hasCheckInStaffAudit(appointment)) {
      try {
        const fetched = await fetchAppointmentCheckInAudit(appointmentId)
        appointment = mergeCheckInAuditFromAppointment(appointment, fetched)
      } catch (e) {
        console.error('Failed to load check-in staff for digital VOS view', e)
      }
    }

    const osiCode = meetingSummary.code ?? meetingSummary.Code
    const [extensionsRes, notesRes] = await Promise.all([
      axios.get(`${API_URL}/Appoinment/extension-requests/${appointmentId}`),
      axios.get(`${API_URL}/Appoinment/osi-submission-activities/${osiCode}`),
    ])

    const extensions = normalizeExtensionRequestsList(extensionsRes.data)
    const notesPayload = notesRes.data as {notes?: Array<Record<string, unknown>>} | null
    const osiNotes = notesPayload?.notes ?? []

    const appointmentCode = String(appointment.code ?? appointment.Code ?? appointmentId)

    return {
      ok: true,
      appointmentCode,
      input: {
        appointment,
        meetingSummary,
        extensions,
        osiNotes,
      },
    }
  } catch (error: unknown) {
    const status = axios.isAxiosError(error) ? error.response?.status ?? 500 : 500
    const message =
      (axios.isAxiosError(error) && typeof error.response?.data === 'object'
        ? (error.response?.data as {text?: string; message?: string})?.text ??
          (error.response?.data as {text?: string; message?: string})?.message
        : null) ??
      (status === 401
        ? 'Authentication failed. Please sign in again.'
        : 'Failed to load digital VOS form data.')

    return {ok: false, status, message}
  }
}
